import React, { useState, useEffect, useCallback } from 'react';

const TYPE_STYLES = {
  success: {
    background: 'var(--green-bg)',
    border: '1px solid rgba(34,197,94,0.35)',
    iconColor: 'var(--green)',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    ),
  },
  error: {
    background: 'var(--red-bg)',
    border: '1px solid rgba(239,68,68,0.35)',
    iconColor: 'var(--red)',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
  warning: {
    background: 'var(--amber-bg)',
    border: '1px solid rgba(245,158,11,0.35)',
    iconColor: 'var(--amber)',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  info: {
    background: 'var(--blue-bg, rgba(59,130,246,0.08))',
    border: '1px solid rgba(59,130,246,0.3)',
    iconColor: 'var(--accent)',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    ),
  },
};

let _nextId = 1;

function ToastItem({ toast, onRemove }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onRemove(toast.id), 300);
  }, [toast.id, onRemove]);

  useEffect(() => {
    // Trigger slide-in
    const t = setTimeout(() => setVisible(true), 16);
    // Auto-dismiss after 4s
    const d = setTimeout(() => dismiss(), 4000);
    return () => { clearTimeout(t); clearTimeout(d); };
  }, [dismiss]);

  const s = TYPE_STYLES[toast.type] || TYPE_STYLES.info;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        minWidth: 300,
        maxWidth: 420,
        padding: '14px 16px',
        borderRadius: 10,
        background: s.background,
        border: s.border,
        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        transform: visible && !leaving ? 'translateX(0)' : 'translateX(calc(100% + 32px))',
        opacity: leaving ? 0 : 1,
        transition: leaving
          ? 'transform 0.3s ease-in, opacity 0.3s ease-in'
          : 'transform 0.3s cubic-bezier(0.16,1,0.3,1), opacity 0.2s ease',
        pointerEvents: 'all',
      }}
    >
      {/* Icon */}
      <div
        style={{
          flexShrink: 0,
          marginTop: 1,
          color: s.iconColor,
        }}
      >
        {s.icon}
      </div>

      {/* Message */}
      <div
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text)',
          lineHeight: 1.5,
          fontFamily: 'var(--font)',
        }}
      >
        {toast.message}
      </div>

      {/* Close */}
      <button
        onClick={dismiss}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          padding: '0 0 0 4px',
          cursor: 'pointer',
          color: 'var(--text3)',
          display: 'flex',
          alignItems: 'center',
          marginTop: 1,
          opacity: 0.7,
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
        aria-label="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

export default function Toast() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    function handler(e) {
      const { message, type = 'info' } = e.detail || {};
      if (!message) return;
      const id = _nextId++;
      setToasts(prev => [...prev, { id, message, type }]);
    }
    window.addEventListener('theclearaudit-toast', handler);
    return () => window.removeEventListener('theclearaudit-toast', handler);
  }, []);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        alignItems: 'flex-end',
        pointerEvents: 'none',
      }}
    >
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={remove} />
      ))}
    </div>
  );
}
